import React from 'react';
import { KeyPressListener } from '../KeyPressListener';
import { Popover } from '../Popover';
import { Stack } from '../Stack';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { Link } from '../Link';
import { DateRangeInput, DateRangeTrigger, DateRangeOptionList, } from './components';
import { MultiViewCalendar, } from '@progress/kendo-react-dateinputs';
import { Keys } from '../../utilities/keyCodes';
import { checkScope } from './utils/checkScope';
import { toDateFormat } from '../../utilities/dateFormat';
export const DateRangePicker = ({ 
// for trigger
size = 'medium', disabled, error, placeholder, required, 
// custom trigger
trigger, 
// for popover
options, direction = 'br', open, disableCloseOnClickOutside, onClickOutside, onOpenChange, autoFlipHorizontally, autoFlipVertically, focusTrapOptions, dateFormat, 
// for calendar
value, defaultValue, displayValue, onChange, maxDate, minDate, focusedDate, 
// for inputs
startPlaceholder = 'Start Date', endPlaceholder = 'End Date', }) => {
    var _a, _b, _c, _d, _e;
    const triggerRef = React.useRef(null);
    const [IError, setIError] = React.useState(error);
    const [inputErrors, setInputErrors] = React.useState({
        start: false,
        end: false,
    });
    const validRange = React.useCallback((d) => {
        if (d === undefined)
            return { start: null, end: null };
        return {
            start: d.start ? toDateFormat.startOfDay(d.start) : null,
            end: d.end ? toDateFormat.endOfDay(d.end) : null,
        };
    }, []);
    const [dateRange, setDateRange] = React.useState(validRange(value));
    const [IValue, setIValue] = React.useState(validRange(value));
    const [triggerValue, setTriggerValue] = React.useState(validRange(value));
    const [openCal, setOpenCal] = React.useState(open);
    const [IsOutOfRange, setIsOutOfRange] = React.useState({
        start: false,
        end: false,
    });
    const minMaxRange = React.useMemo(() => {
        return {
            min: minDate && toDateFormat.startOfDay(minDate),
            max: maxDate && toDateFormat.endOfDay(maxDate),
        };
    }, [minDate, maxDate]);
    // Checks min and max dates for start and end date
    const checkDateRangeScope = React.useCallback((range) => {
        var _a, _b;
        const isOutOfScope = checkScope(range, minDate, maxDate);
        setIsOutOfRange({
            start: isOutOfScope.startOutOfScope
                ? `Min date is ${toDateFormat.numeric((_a = toDateFormat.startOfDay(minDate)) === null || _a === void 0 ? void 0 : _a.getTime())}`
                : false,
            end: isOutOfScope.endOutOfScope
                ? `Max date is ${toDateFormat.numeric((_b = toDateFormat.endOfDay(maxDate)) === null || _b === void 0 ? void 0 : _b.getTime())}`
                : false,
        });
    }, [maxDate, minDate]);
    // this functions as a stopper to only run calendar onChange when 2 onChanges conflict
    let preventOnChange = false;
    // handler for MultiViewCalendar onChange
    const handleCalendarOnChange = (event) => {
        const eventValue = event.target.value;
        setDateRange(eventValue);
        setInputErrors({ start: false, end: false });
        setIsOutOfRange({ start: false, end: false });
        preventOnChange = true;
    };
    // handler for DateInput onBlur
    const handleDateOnBlur = (type, data) => {
        if (!preventOnChange) {
            setDateRange(Object.assign(Object.assign({}, dateRange), { [type]: data }));
            checkDateRangeScope(Object.assign(Object.assign({}, dateRange), { [type]: data }));
        }
    };
    // confirm handler, this returns dateRange value through onChange
    const handleOnConfirm = () => {
        onChange(validRange(dateRange));
        setTriggerValue(dateRange);
        setIValue(dateRange);
        setOpenCal(false);
    };
    // close popover function - this will revert dateRange to initial value
    const close = () => {
        if (openCal) {
            setDateRange(IValue);
            setOpenCal(false);
            !trigger && triggerRef.current.focus();
        }
    };
    const handleOnClickOutside = () => {
        if (openCal) {
            onClickOutside === null || onClickOutside === void 0 ? void 0 : onClickOutside();
            if (!disableCloseOnClickOutside) {
                setDateRange(IValue);
                setOpenCal(false);
            }
        }
    };
    // toggle open state
    const toggleOpen = () => {
        openCal ? close() : setOpenCal(!openCal);
    };
    // keyboard event for end date input to confirm and close
    const calendarKeyPress = (event) => {
        if (event.code === Keys.Space && !isApplyDisabled) {
            handleOnConfirm();
            event.preventDefault();
        }
    };
    const clearDateRange = (d) => {
        close();
        onChange === null || onChange === void 0 ? void 0 : onChange(d);
        setDateRange(d);
        setIValue(d);
        checkDateRangeScope(d);
    };
    // keyboard event for trigger to open popover
    const triggerKeyPress = (event) => {
        (event.code === Keys.Enter || event.code === Keys.Space) &&
            setOpenCal(true);
    };
    // Update internal states with incoming value
    React.useEffect(() => {
        setDateRange(validRange(value));
        setIValue(validRange(value));
        setTriggerValue(validRange(value));
        checkDateRangeScope(value);
    }, [checkDateRangeScope, validRange, value]);
    // Handles when open prop changes
    React.useEffect(() => {
        setOpenCal(open);
    }, [open]);
    // Triggers whenever openCal changes
    React.useEffect(() => {
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(openCal);
    }, [onOpenChange, openCal]);
    // Update error states when out of scope state changes
    React.useEffect(() => {
        setInputErrors({ start: IsOutOfRange.start, end: IsOutOfRange.end });
    }, [IsOutOfRange, minMaxRange]);
    // Update trigger error on apply && initial load
    React.useEffect(() => {
        if (inputErrors.start || inputErrors.end) {
            setIError(true);
        }
    }, [IValue, inputErrors]);
    const hasError = inputErrors.start || inputErrors.end;
    const hasNewValue = ((_a = IValue === null || IValue === void 0 ? void 0 : IValue.start) === null || _a === void 0 ? void 0 : _a.getTime()) !== ((_b = dateRange === null || dateRange === void 0 ? void 0 : dateRange.start) === null || _b === void 0 ? void 0 : _b.getTime()) ||
        ((_c = IValue === null || IValue === void 0 ? void 0 : IValue.end) === null || _c === void 0 ? void 0 : _c.getTime()) !== ((_d = dateRange === null || dateRange === void 0 ? void 0 : dateRange.end) === null || _d === void 0 ? void 0 : _d.getTime());
    const hasAcceptableValues = (dateRange.start && dateRange.end) ||
        (dateRange.start === null && dateRange.end === null);
    const isApplyDisabled = (!hasAcceptableValues ||
        !hasNewValue ||
        hasError ||
        !IsOutOfRange);
    const triggerProps = {
        onClick: toggleOpen,
    };
    return (React.createElement(Popover, { open: !disabled && openCal, onClickOutside: handleOnClickOutside, trigger: (_e = trigger === null || trigger === void 0 ? void 0 : trigger(triggerValue, triggerProps, openCal, IError)) !== null && _e !== void 0 ? _e : (React.createElement(DateRangeTrigger, { open: openCal, placeholder: placeholder !== null && placeholder !== void 0 ? placeholder : 'Select Date Range', onClick: toggleOpen, onKeyDown: triggerKeyPress, onChange: clearDateRange, value: triggerValue, displayValue: displayValue, disabled: disabled, error: IError, dateFormat: dateFormat, size: size, ref: triggerRef, required: required })), direction: direction, padding: null, width: "auto", className: "DateRangePicker__popover", portal: true, focusTrapOptions: focusTrapOptions, autoFlipHorizontally: autoFlipHorizontally, autoFlipVertically: autoFlipVertically },
        React.createElement(KeyPressListener, { code: Keys.Esc, handler: close, disabled: disableCloseOnClickOutside || !openCal }),
        React.createElement("div", { className: "d-f flex-row" },
            options && options.length > 0 && (React.createElement(DateRangeOptionList, { options: options, min: minMaxRange.min, max: minMaxRange.max, onChange: (data) => {
                    setTriggerValue(data);
                    setDateRange(data);
                    onChange(data);
                    setOpenCal(false);
                } })),
            React.createElement("div", { className: "DateRangePicker" },
                React.createElement("div", { className: "DateRangePicker__header" },
                    React.createElement("div", { className: "DateRangePicker__inputGroup" },
                        React.createElement(DateRangeInput, { placeholder: startPlaceholder, value: dateRange === null || dateRange === void 0 ? void 0 : dateRange.start, error: inputErrors.start, dateFormat: dateFormat, onBlur: (value) => {
                                handleDateOnBlur('start', value);
                            } }),
                        React.createElement(Icon, { name: "arrow_forward", className: "DateRangePicker__fromTo" }),
                        React.createElement(DateRangeInput, { placeholder: endPlaceholder, value: dateRange === null || dateRange === void 0 ? void 0 : dateRange.end, error: inputErrors.end, dateFormat: dateFormat, onBlur: (value) => {
                                handleDateOnBlur('end', value);
                            } }))),
                React.createElement("div", { onKeyPress: calendarKeyPress, className: "DatePicker__calendar" },
                    React.createElement(MultiViewCalendar, { mode: "range", views: 1, value: dateRange, onChange: handleCalendarOnChange, focusedDate: focusedDate, defaultValue: defaultValue, min: minMaxRange.min, max: minMaxRange.max })))),
        React.createElement(Stack, { className: "DateRangePicker__actions", direction: "row-reverse", justifyContent: "space-between" },
            React.createElement(Stack, { direction: "row-reverse", spacing: 2 },
                React.createElement(Button, { disabled: isApplyDisabled, onClick: handleOnConfirm, small: true, color: "primary" }, "Apply"),
                React.createElement(Button, { small: true, fill: "subtle", onClick: close }, "Cancel")),
            React.createElement(Link, { primary: true, onClick: () => setDateRange({ start: null, end: null }), disabled: !((dateRange === null || dateRange === void 0 ? void 0 : dateRange.start) || (dateRange === null || dateRange === void 0 ? void 0 : dateRange.end)), className: "fs-2" }, "Clear"))));
};
//# sourceMappingURL=DateRangePicker.js.map